/**
 * useResetPassword Hook
 * 
 * Custom hook for managing reset password state and logic.
 */

import * as React from "react";
import { userService } from "@/services/userService";
import { toast } from "sonner";

interface UseResetPasswordProps {
  userId: string;
  userIar?: string;
  userName?: string;
  callsign?: string;
  onSuccess?: () => void;
}

export function useResetPassword({
  userId,
  userIar,
  userName,
  callsign,
  onSuccess,
}: UseResetPasswordProps) {
  const [passwordType, setPasswordType] = React.useState<"iar" | "custom">("iar");
  const [customPassword, setCustomPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const resetState = React.useCallback(() => {
    setPasswordType("iar");
    setCustomPassword("");
    setShowPassword(false);
    setError(null);
    setSuccess(false);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let passwordToSet: string;

      if (passwordType === "iar") {
        if (!userIar) {
          setError("IAR value tidak tersedia untuk pengguna ini");
          setLoading(false);
          return;
        }
        passwordToSet = userIar;
      } else {
        if (!customPassword || customPassword.trim() === "") {
          setError("Password harus diisi");
          setLoading(false);
          return;
        }
        passwordToSet = customPassword;
      }

      await userService.resetPassword(userId, passwordToSet);

      setSuccess(true);
      toast.success("Password berhasil direset!", {
        description: `Password untuk ${callsign || userName || "pengguna"} telah berhasil direset.`,
      });
      
      onSuccess?.();
    } catch (err) {
      console.error("Error resetting password:", err);
      const errorMessage = err instanceof Error ? err.message : "Gagal mereset password";
      setError(errorMessage);
      toast.error("Gagal mereset password", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [passwordType, userIar, customPassword, userId, callsign, userName, onSuccess]);

  const canSubmit = React.useMemo(() => {
    if (loading) return false;
    if (passwordType === "iar" && !userIar) return false;
    if (passwordType === "custom" && !customPassword.trim()) return false;
    return true;
  }, [loading, passwordType, userIar, customPassword]);

  return {
    passwordType,
    setPasswordType,
    customPassword,
    setCustomPassword,
    showPassword,
    setShowPassword,
    loading,
    error,
    success,
    resetState,
    handleSubmit,
    canSubmit,
  };
}

